/**
 * פלטת הגרפים של "תמונת מצב" — נגזרת מהטוקנים של המערכת (index.scss):
 * יין #681c3c, פלדה #3d608a / #2f4d72, טורקיז #12727B.
 *
 * הערכים כאן הם מחרוזות צבע רגילות ולא משתני CSS, כי הם נכנסים ל-fill/stroke
 * של SVG ולחישובי שקיפות (fade) שדורשים ערך ממשי.
 */

export const CHART = {
  wine: '#681c3c',
  wineLift: '#8f3158',
  plum: '#a8567a',
  steel: '#2f4d72',
  steelMid: '#3d608a',
  steelSoft: '#7fa3cc',
  teal: '#12727b',
  tealSoft: '#5cb0b6',
  brass: '#b5762a',
  brassSoft: '#dcab63',
  grid: '#efe8eb',
  sunk: '#f6f1f3',
  surface: '#ffffff',
  ink: '#241d20',
  ink2: '#5a4e53',
  ink3: '#8b7c82',
  good: '#0f7a56',
  warn: '#a86a12',
  crit: '#b3352e',
} as const;

/** סדר הצבעים לסדרות מרובות (עמודות/פרוסות). */
export const CHART_SERIES: string[] = [CHART.wine, CHART.steelMid, CHART.teal, CHART.brass, CHART.plum, CHART.steelSoft];

/** צבע עם שקיפות — מחליף color-mix, שלא נתמך בכל מנועי ה-SVG. */
export const fade = (hex: string, alpha: number): string => {
  const h = hex.replace('#', '');
  const v =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h;
  const r = parseInt(v.slice(0, 2), 16);
  const g = parseInt(v.slice(2, 4), 16);
  const b = parseInt(v.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
};

export const nf = (n: number): string => Math.round(n).toLocaleString('he-IL');

/**
 * סימן המטבע של קוד ISO. הקרן מדווחת ב-ILS או ב-USD (`balance.currency`), ולכן
 * הסימן אינו קבוע — קרן דולרית שמוצגת עם ₪ מציגה מספר שקרי.
 * הפורמטרים נשמרים במפה כי הם נבנים לכל תווית ציר בנפרד.
 */
const symbolCache: Record<string, string> = {};
export const currencySymbol = (code = 'ILS'): string => {
  if (!symbolCache[code]) {
    try {
      symbolCache[code] =
        new Intl.NumberFormat('he-IL', { style: 'currency', currency: code }).formatToParts(0).find((part) => part.type === 'currency')?.value ?? code;
    } catch {
      symbolCache[code] = code;
    }
  }
  return symbolCache[code];
};

export const money = (n: number, code = 'ILS'): string => currencySymbol(code) + ' ' + nf(n);

/** סכום מקוצר לצירים ולתוויות צפופות: ₪1.2M, ₪84K. */
export const kMoney = (n: number, code = 'ILS'): string => {
  const s = currencySymbol(code);
  const a = Math.abs(n);
  if (a >= 1e6) return s + (n / 1e6).toFixed(a >= 1e7 ? 1 : 2).replace(/\.?0+$/, '') + 'M';
  if (a >= 1000) return s + Math.round(n / 1000) + 'K';
  return s + nf(n);
};

export const intFmt = (n: number): string => nf(n);

/** מעגל את קצה הציר למספר "עגול" כדי שהתוויות יהיו קריאות. */
export const niceMax = (v: number): number => {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / mag;
  const step = n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 4 ? 4 : n <= 5 ? 5 : n <= 8 ? 8 : 10;
  return step * mag;
};

/**
 * תווית ציר X בלי אותיות עבריות מוצגת LTR. בלי זה טווח כמו "₪100–360"
 * מתהפך ל-"360–100" בתוך פסקה RTL, כי המקף הוא תו ניטרלי.
 */
export const isLatinLabel = (s: string): boolean => !/[֐-׿]/.test(s);
